import classNames from "classnames/bind";
import React, { useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { BreakpointContext } from "../../../Application/components/context/BreakpointContext";
import { ScrollPoint } from "../../../Application/hooks/useScrollPoint";
import { LazyLoading } from "../../../Application/layout";
import { getParamValue } from "../../../Application/utils/routing";
import { SEARCH_TERM } from "../../constants";
import { type ContentItem } from "../../types";
import NavigatableContentItem from "./NavigatableContentItem";

import { type ActionCreatorWithoutPayload } from "@reduxjs/toolkit";
import { OnClickOutside } from "../../../Account/components/OnClickOutside/OnClickOutside";
import { type RootState } from "../../../Application/store/store";
import { RStatus } from "../../../Application/types";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";

import styles from "./ContentList.module.scss";

dayjs.extend(advancedFormat);

const cx = classNames.bind(styles);

interface Props {
  dropdown?: React.ReactNode;
  selectResult: (state: RootState) => ContentItem[];
  selectStatus: (state: RootState) => RStatus;
  selectError: (state: RootState) => string | undefined;
  selectReachedEnd: (state: RootState) => boolean;
  fetchMore: ActionCreatorWithoutPayload<string>;
  hideTitle?: boolean;
  closeDropdown?: (e: MouseEvent | TouchEvent) => void;
  interactive?: boolean;
  emptyStatePage?: React.ReactNode;
}

interface DateSpacer {
  isDateSpacer: boolean;
  date: string;
}

export const ContentList: React.FC<Props> = ({
  dropdown,
  closeDropdown,
  selectResult,
  selectStatus,
  selectError,
  selectReachedEnd,
  fetchMore,
  hideTitle,
  interactive,
  emptyStatePage,
}) => {
  const { t } = useTranslation(["content"]);
  const { isMobileScreen } = useContext(BreakpointContext);
  const location = useLocation();

  const searchResults = useSelector(selectResult);

  const status = useSelector(selectStatus);
  const errorMessage = useSelector(selectError);
  const end = useSelector(selectReachedEnd);

  let query = "";
  if (location.search) {
    query = getParamValue(location.search, SEARCH_TERM);
  }

  const dispatch = useDispatch();

  const fetch = useCallback(() => {
    dispatch(fetchMore());
  }, [dispatch, fetchMore]);

  const handleRenderItem = useCallback(
    (item: ContentItem) => {
      return (
        <NavigatableContentItem
          key={item.uuid ? item.uuid : item.id}
          item={item}
          interactive={item.expired ? false : interactive}
        />
      );
    },
    [interactive],
  );

  const renderDateSpacer = (date: string) => (
    <div className={styles.dateSpacer}>{dayjs(date).format("dddd, MMMM Do, YYYY")}</div>
  );

  const groupedResults = useMemo(() => {
    let lastDate: string | null = null;
    const results = searchResults.reduce((acc: Array<ContentItem | DateSpacer>, item) => {
      if (item.progressDatetime) {
        const currentDate = dayjs(item.progressDatetime).format("dddd, MMMM Do, YYYY");
        if (currentDate !== lastDate) {
          lastDate = currentDate;
          acc.push({ isDateSpacer: true, date: item.progressDatetime });
        }
      }
      acc.push(item);
      return acc;
    }, []);
    return results;
  }, [searchResults]);

  return (
    <div>
      <section className={styles.header}>
        {!hideTitle && (
          <span className={styles.title}>
            {query.length
              ? t("results.resultsFor", {
                  count: searchResults.length,
                  query,
                })
              : t("common.all")}
          </span>
        )}
        {dropdown && (
          <div className={cx({ [styles.sortByContainer]: !isMobileScreen })}>
            <label
              className={cx(styles.sortByLabel, {
                [styles.hidden]: isMobileScreen,
              })}
            >
              {t("sort.label")}
            </label>
            {closeDropdown && isMobileScreen ? (
              <OnClickOutside handler={closeDropdown}>{dropdown}</OnClickOutside>
            ) : (
              dropdown
            )}
          </div>
        )}
      </section>
      <section className={styles.body}>
        {status === RStatus.Got && searchResults.length === 0 && emptyStatePage}
        <LazyLoading
          content={groupedResults}
          status={status}
          reachedEnd={end}
          scrollContainer={<ScrollPoint callback={fetch} />}
          errorMessage={errorMessage}
          renderItem={(item: ContentItem | DateSpacer) => {
            if ("isDateSpacer" in item) {
              return renderDateSpacer(item.date);
            }
            return handleRenderItem(item);
          }}
        />
      </section>
    </div>
  );
};
